/**
 * Serviço de protótipo para sessão de assinatura digital.
 *
 * Objetivo:
 * - Simular o ciclo de vida de uma sessão de assinatura (iniciar, encerrar, ler estado atual)
 *   de forma semelhante ao padrão usado em `modules/prototype-onboard`.
 *
 * Implementação:
 * - Persiste apenas um snapshot simples da sessão no `localStorage`.
 * - Usa uma pequena latência artificial para aproximar o comportamento de rede.
 *
 * Importante:
 * - Este serviço é puramente de protótipo; substituir por chamadas HTTP reais no futuro.
 */

import type {
  PrototypeSignatureProvider,
  PrototypeSignatureSession,
} from "./types";

const STORAGE_KEY = "@fluxmed:prototype-signature:session";

const PROVIDER_LABEL_MAP: Record<PrototypeSignatureProvider, string> = {
  VIDAAS: "Vidaas",
  SAFEID: "SafeId",
} as const;

/**
 * Simula latência de rede para aproximar o comportamento real de serviço.
 */
function simulateNetworkDelay(ms = 400): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Constrói um snapshot de sessão de assinatura para o provedor informado.
 *
 * Observação:
 * - O campo `remainingLabel` é estático e serve apenas para prototipagem visual.
 */
function buildSession(
  provider: PrototypeSignatureProvider,
): PrototypeSignatureSession {
  return {
    provider,
    providerLabel: PROVIDER_LABEL_MAP[provider],
    remainingLabel: "14:32",
  };
}

/**
 * Retorna a sessão de assinatura atualmente armazenada, se existir.
 */
async function getCurrentSession(): Promise<PrototypeSignatureSession | null> {
  await simulateNetworkDelay(250);

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored) as PrototypeSignatureSession;
    if (!parsed || !parsed.provider) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Inicia uma nova sessão de assinatura para o provedor selecionado.
 *
 * Estratégia futura:
 * - Substituir por chamada HTTP real para criação de sessão.
 */
async function startSession(
  provider: PrototypeSignatureProvider,
): Promise<PrototypeSignatureSession> {
  await simulateNetworkDelay();

  const session = buildSession(provider);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

/**
 * Encerra a sessão de assinatura em andamento, se existir.
 *
 * Estratégia futura:
 * - Substituir por chamada HTTP real para encerramento de sessão.
 */
async function endSession(): Promise<void> {
  await simulateNetworkDelay(300);
  window.localStorage.removeItem(STORAGE_KEY);
}

export const prototypeSignatureService = {
  getCurrentSession,
  startSession,
  endSession,
};

